/**
 * SiblingComposer (13-26): "Add to the record", one log row at a time (FIELDSET §1.7, §3.4.1).
 *
 * - **Kinds:** only the rows the handling verb writes on this register (`composerKinds`: §3.4.1's map, the live
 *   `EntryType` / `LogKind` values verbatim, labels in plain words). Nothing is pre-selected.
 * - **Inputs:** what happened (a Note, 4,000), when it happened (a Melbourne day, default today, and an optional time),
 *   and for an investigation step the investigator (a Harmony address, default me; the row's `ActorUPN`, G-57) and
 *   their position (a role, never a name, §1.5).
 * - **Save:** 13-20's engine (`buildRecordVerb('handling', …)` with `rows` only) builds the row and screens the text:
 *   a phone number or email is refused, "Contact details go in their own fields" (§1.6). A rows-only save writes no
 *   parent column; it goes through `writer.commitRecordVerb` with an empty patch and the handling gate (13-23), after
 *   a confirm (D-13-L-4). `first_action_on` reads the earliest action or investigation step this writes (ISS-202).
 * - **13-27:** the notification panel reuses it for the regulator rows: `verb = 'notification'`, one fixed kind per
 *   button (`kinds`), and the notice the gate is asked about (`gate`, D-13-22-5). The defaults are unchanged.
 * - **13-28:** the WHS panel saves its rows under the WHS verb (`verb = 'whs'`: site preserved, a return-to-work step),
 *   and may ask questions before the text (`prompts`, written at the head of the Note). The defaults are unchanged.
 * Practice mode: the runtime's practice writer stores the row in the practice record only.
 */

import { useId, useMemo, useState } from 'react';
import { Button, Input, Select, TextArea } from '../ui';
import { verbsOf } from '../../config/fieldset/registerFields';
import { useActionRuntime } from '../../hooks/useActionRuntime';
import { formatDay, melbourneWallToInstant, formatInstant, todayMelbourne } from '../../lib/fieldValues';
import { RECORD_AUTHORITY_TEXT } from '../../lib/recordAuthority';
import type { RecordAuthorityContext } from '../../lib/recordAuthority';
import { buildRecordVerb } from '../../lib/recordVerbs';
import { composerInputOf, composerKinds, composerRefusals, composerRow, isInvestigationStep, promptRefusals, promptedText } from '../../lib/siblingComposer';
import type { ComposerInput, ComposerKind, ComposerPrompt } from '../../lib/siblingComposer';
import { RecordConflictError, RecordRefusedError } from '../../services/recordService';
import type { RecordIO } from '../../services/recordService';
import type { Actor } from '../../types/actionRegister';
import type { RecordRow, SiblingDraft } from '../../types/records';
import type { VerbSaved } from './VerbForm';
import styles from './record.module.css';

export interface SiblingComposerProps {
  row: RecordRow;
  actor: Actor;
  onSaved: (saved: VerbSaved) => void;
  /** 13-27: the verb the rows are saved under (default the handling verb); 13-28 adds the WHS verb. */
  verb?: 'handling' | 'notification' | 'whs';
  /** 13-27: the kinds offered (default the handling verb's, `composerKinds`). One kind is fixed: no picker. */
  kinds?: readonly ComposerKind[];
  /** 13-27: the gate's facts (the notice a notification row belongs to). */
  gate?: RecordAuthorityContext;
  /** 13-27: the card's heading and first line. */
  title?: string;
  intro?: string;
  /** 13-28: questions asked before the text; each answer heads the Note as `<label> <answer>`. */
  prompts?: readonly ComposerPrompt[];
  /** 13-28: the text box's label (default "What happened"). */
  textLabel?: string;
}

type Phase = 'edit' | 'confirm' | 'saving' | 'saved';

const TEXT_CAP = 4000;
const fmt = (n: number) => n.toLocaleString('en-AU');

/** When it happened, in words: the day, with the Melbourne time when one is entered. */
function whenWords(day: string, time: string): string | null {
  if (!day) return null;
  if (time) {
    const r = melbourneWallToInstant(day, time);
    return r.ok ? formatInstant(r.value) : null;
  }
  const label = formatDay(day);
  return label === '—' ? null : label;
}

export default function SiblingComposer({
  row,
  actor,
  onSaved,
  verb = 'handling',
  kinds: given,
  gate,
  title,
  intro,
  prompts,
  textLabel,
}: SiblingComposerProps) {
  const { writer, io } = useActionRuntime();
  const uid = useId();
  const register = row.register;
  const kinds = useMemo(() => given ?? composerKinds(register), [given, register]);
  // A caller that fixes one kind (13-27) gets no picker; the default list keeps its picker even with one kind (Risk).
  const fixed = given && given.length === 1 ? given[0] : null;
  const [today] = useState(() => todayMelbourne(new Date()));

  const [kindKey, setKindKey] = useState(fixed ? fixed.key : '');
  const [text, setText] = useState('');
  const [day, setDay] = useState(today);
  const [time, setTime] = useState('');
  const [investigator, setInvestigator] = useState(actor.upn);
  const [position, setPosition] = useState('');
  const [phase, setPhase] = useState<Phase>('edit');
  const [refusals, setRefusals] = useState<Partial<Record<ComposerInput, string>>>({});
  const [built, setBuilt] = useState<{ siblings: SiblingDraft[]; when: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [savedPending, setSavedPending] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [promptRefused, setPromptRefused] = useState<Record<string, string>>({});

  const kind: ComposerKind | undefined = kinds.find((k) => k.key === kindKey);
  const step = isInvestigationStep(kind);
  const busy = phase === 'saving';
  const ready = io !== null;
  const idOf = (name: string) => `${uid}-${name}`;
  const reviewedElsewhere = verb === 'handling' && verbsOf(register).includes('riskReview');

  function touch(input: ComposerInput) {
    setRefusals((r) => {
      const next = { ...r };
      delete next[input];
      return next;
    });
    setBuilt(null);
    if (phase !== 'edit') setPhase('edit');
    setFailure(null);
  }

  function review() {
    setFailure(null);
    if (!kind) {
      setRefusals({ kind: 'Choose what you are adding.' });
      return;
    }
    const form = { kind, text: prompts ? promptedText(prompts, answers, text) : text, day, time, investigator, position };
    const missing = Object.fromEntries(promptRefusals(prompts ?? [], answers).map((r) => [r.key, r.message]));
    setPromptRefused(missing);
    const found: Partial<Record<ComposerInput, string>> = {};
    for (const r of composerRefusals(form, actor.upn)) found[composerInputOf(r.code)] ??= r.message;
    const result = buildRecordVerb(
      verb,
      row,
      { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) },
      { rows: [composerRow(form, today, actor.upn)], auditId: crypto.randomUUID() },
      new Date(),
    );
    if (!result.ok) for (const r of result.refusals) found[composerInputOf(r.code)] ??= r.message;
    if (Object.keys(found).length > 0 || Object.keys(missing).length > 0 || !result.ok) {
      setRefusals(found);
      setBuilt(null);
      setPhase('edit');
      return;
    }
    // A log row never changes the record itself (FIELDSET §1.7): a patch here would be a bug.
    if (Object.keys(result.patch).length > 0) throw new Error('SiblingComposer: a log row must not write a parent column');
    setRefusals({});
    setBuilt({ siblings: result.siblings, when: day === today && !time ? 'Now' : (whenWords(day, time) ?? day) });
    setPhase('confirm');
  }

  async function save() {
    if (!built || !io) return;
    setPhase('saving');
    setFailure(null);
    try {
      const r = await writer.commitRecordVerb(io as RecordIO, row.ref, row.eTag, { patch: {}, siblings: built.siblings }, actor, {
        verb,
        row,
        ctx: { ...(gate ?? {}), columns: [] },
      });
      const info: VerbSaved = { verb, ref: row.ref, mode: writer.mode, auditPending: r.auditPending.length };
      setSavedPending(info.auditPending);
      setText('');
      setAnswers({});
      setPosition('');
      setTime('');
      setDay(today);
      setBuilt(null);
      setPhase('saved');
      onSaved(info);
    } catch (err) {
      if (err instanceof RecordConflictError) {
        setFailure('Someone else changed this record. Reload it, then add this again.');
      } else if (err instanceof RecordRefusedError) {
        setFailure(RECORD_AUTHORITY_TEXT[err.reason]);
      } else {
        setFailure(err instanceof Error ? err.message : 'The entry did not go through.');
      }
      setPhase('confirm');
    }
  }

  const msg = (input: ComposerInput) =>
    refusals[input] ? (
      <p id={idOf(`${input}-msg`)} className={styles.refusal}>
        {refusals[input]}
      </p>
    ) : null;
  const refused = (input: ComposerInput) => (refusals[input] ? styles.inputRefused : undefined);
  const described = (input: ComposerInput) => (refusals[input] ? idOf(`${input}-msg`) : undefined);
  const echo = whenWords(day, time);

  return (
    <section className={styles.composer} aria-labelledby={idOf('title')}>
      <h4 id={idOf('title')} className={styles.composerTitle}>
        {title ?? 'Add to the record'}
      </h4>
      <p className={styles.hint}>
        {intro ??
          'Each entry is its own row on the trail, under your name. Actions and investigation steps are what the first-action date is read from.'}
      </p>

      <fieldset className={styles.fields} disabled={busy} inert={busy || undefined}>
        {!fixed && (
        <div className={styles.field}>
          <label htmlFor={idOf('kind')} className={styles.label}>
            What are you adding?
          </label>
          <Select
            id={idOf('kind')}
            value={kindKey}
            className={refused('kind')}
            aria-describedby={described('kind')}
            onChange={(e) => {
              setKindKey(e.target.value);
              touch('kind');
            }}
          >
            <option value="">Choose one</option>
            {kinds.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </Select>
          {reviewedElsewhere && <p className={styles.hint}>A review is recorded with Risk review, under Closure.</p>}
          {msg('kind')}
        </div>
        )}
        {fixed && msg('kind')}

        {(prompts ?? []).map((p) => {
          const id = idOf(`p-${p.key}`);
          const value = answers[p.key] ?? '';
          const bad = promptRefused[p.key];
          const set = (v: string) => {
            setAnswers((a) => ({ ...a, [p.key]: v }));
            setPromptRefused((r) => {
              const next = { ...r };
              delete next[p.key];
              return next;
            });
            touch('text');
          };
          return (
            <div key={p.key} className={styles.field}>
              <label htmlFor={id} className={styles.label}>
                {p.label}
              </label>
              {p.choices ? (
                <Select id={id} value={value} className={bad ? styles.inputRefused : undefined} aria-describedby={bad ? `${id}-msg` : undefined} onChange={(e) => set(e.target.value)}>
                  <option value="">Not answered</option>
                  {p.choices.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input id={id} value={value} className={bad ? styles.inputRefused : undefined} aria-describedby={bad ? `${id}-msg` : undefined} onChange={(e) => set(e.target.value)} />
              )}
              {p.hint && <p className={styles.hint}>{p.hint}</p>}
              {bad && (
                <p id={`${id}-msg`} className={styles.refusal}>
                  {bad}
                </p>
              )}
            </div>
          );
        })}

        <div className={styles.field}>
          <label htmlFor={idOf('text')} className={styles.label}>
            {textLabel ?? (step ? 'What the step was, and what it found' : 'What happened')}
          </label>
          <TextArea
            id={idOf('text')}
            rows={4}
            value={text}
            className={refused('text')}
            aria-describedby={described('text')}
            onChange={(e) => {
              setText(e.target.value);
              touch('text');
            }}
          />
          <div className={styles.under}>
            <span className={`${styles.count} ${text.length > TEXT_CAP ? styles.countOver : ''}`} aria-live="polite">
              {fmt(text.length)} / {fmt(TEXT_CAP)}
            </span>
          </div>
          <p className={styles.hint}>No phone numbers or email addresses here: contact details go in their own fields.</p>
          {msg('text')}
        </div>

        <div className={styles.field}>
          <label htmlFor={idOf('day')} className={styles.label}>
            When it happened
          </label>
          <div className={styles.dateRow}>
            <Input
              id={idOf('day')}
              type="date"
              value={day}
              className={`${styles.dateInput} ${refused('when') ?? ''}`}
              aria-describedby={[described('when'), idOf('day-echo')].filter(Boolean).join(' ')}
              onChange={(e) => {
                setDay(e.target.value);
                touch('when');
              }}
            />
            <Input
              id={idOf('time')}
              type="time"
              value={time}
              className={styles.timeInput}
              aria-label="When it happened: time (optional)"
              onChange={(e) => {
                setTime(e.target.value);
                touch('when');
              }}
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => {
                setDay(today);
                setTime('');
                touch('when');
              }}>
              Today
            </Button>
          </div>
          <p id={idOf('day-echo')} className={styles.hint}>
            {day === today && !time ? 'Today; dated when you add it.' : (echo ?? 'Enter the date.')}
          </p>
          {msg('when')}
        </div>

        {step && (
          <>
            <div className={styles.field}>
              <label htmlFor={idOf('investigator')} className={styles.label}>
                Investigator
              </label>
              <div className={styles.inline}>
                <Input
                  id={idOf('investigator')}
                  type="email"
                  value={investigator}
                  placeholder="Their Harmony email address"
                  className={refused('investigator')}
                  aria-describedby={described('investigator')}
                  onChange={(e) => {
                    setInvestigator(e.target.value);
                    touch('investigator');
                  }}
                />
                <Button type="button" variant="secondary" size="sm" onClick={() => {
                    setInvestigator(actor.upn);
                    touch('investigator');
                  }}>
                  Me
                </Button>
              </div>
              {msg('investigator')}
            </div>
            <div className={styles.field}>
              <label htmlFor={idOf('position')} className={styles.label}>
                Their position
              </label>
              <Input
                id={idOf('position')}
                value={position}
                className={refused('position')}
                aria-describedby={described('position')}
                onChange={(e) => {
                  setPosition(e.target.value);
                  touch('position');
                }}
              />
              <p className={styles.hint}>A role, not a name.</p>
              {msg('position')}
            </div>
          </>
        )}
      </fieldset>

      {phase === 'confirm' && built && kind && (
        <div className={styles.confirm} role="status">
          <p className={styles.confirmTitle}>Check before you add it</p>
          <ul className={styles.changeList}>
            <li>
              <span className={styles.changeLabel}>Entry:</span> <strong>{kind.label}</strong>
            </li>
            <li>
              <span className={styles.changeLabel}>When:</span> {built.when}
            </li>
            {step && (
              <li>
                <span className={styles.changeLabel}>Investigator:</span> {built.siblings[0]?.ActorUPN}
                {position.trim() ? `, ${position.trim()}` : ''}
              </li>
            )}
          </ul>
          <p className={styles.hint}>1 audit row will be written under your name. The record's own fields don't change.</p>
        </div>
      )}

      {failure && (
        <p className={styles.failure} role="alert">
          Couldn't add that: {failure}
        </p>
      )}

      {phase === 'saved' && (
        <p className={styles.saved} role="status">
          <span className={styles.tick} aria-hidden="true">
            ✓
          </span>{' '}
          {savedPending > 0 ? `Added; ${savedPending} audit ${savedPending === 1 ? 'row' : 'rows'} will be posted.` : 'Added to the record'}
          {writer.mode === 'practice' && <span className={styles.hint}> Practice mode: nothing was written to SharePoint.</span>}
        </p>
      )}

      <div className={styles.actions}>
        {!ready && <span className={styles.hint}>Connecting to SharePoint…</span>}
        {phase === 'confirm' || phase === 'saving' ? (
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setPhase('edit')}>
              Change it
            </Button>
            <Button type="button" loading={busy} disabled={!ready} onClick={() => void save()}>
              Add to the record
            </Button>
          </>
        ) : (
          <Button type="button" variant="secondary" disabled={!ready} onClick={review}>
            Review the entry
          </Button>
        )}
      </div>
    </section>
  );
}
